import { type WandElement, type WandPoint } from './wandGeometry';

export type PieSector = {
  cx: number;
  cy: number;
  r: number;
  r0?: number;
  startAngle: number;
  endAngle: number;
};

export type ChartOption = {
  series?: Array<{
    id?: unknown;
    type?: unknown;
    name?: unknown;
    data?: unknown;
    yAxisIndex?: unknown;
  }>;
  xAxis?: Array<{ type?: unknown; data?: Array<unknown> }>;
  yAxis?: Array<{ type?: unknown; data?: Array<unknown> }>;
};

export type PixelChart = {
  getOption: () => ChartOption;
  convertToPixel: (
    finder: { seriesIndex?: number; xAxisIndex?: number; yAxisIndex?: number },
    value: unknown,
  ) => unknown;
  pieSector?: (seriesIndex: number, dataIndex: number) => PieSector | null;
};

type Origin = { left: number; top: number; width: number; height: number };

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function seriesName(series: { name?: unknown }): string {
  return typeof series.name === 'string' && series.name.trim() ? series.name : 'mark';
}

function categoryAt(categories: Array<unknown> | undefined, index: number): string {
  const value = categories?.[index];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function toPixel(chart: PixelChart, seriesIndex: number, yAxisIndex: number, value: unknown): [number, number] | null {
  const finders = [{ seriesIndex }, { xAxisIndex: 0, yAxisIndex }];
  for (const finder of finders) {
    try {
      const pixel = chart.convertToPixel(finder, value);
      if (
        Array.isArray(pixel) &&
        pixel.length >= 2 &&
        pixel.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
      ) {
        return [pixel[0], pixel[1]];
      }
    } catch {
      continue;
    }
  }
  return null;
}

function categoryOnY(option: ChartOption): boolean {
  const xType = option.xAxis?.[0]?.type;
  const yType = option.yAxis?.[0]?.type;
  return yType === 'category' && xType !== 'category';
}

function tuple(vertical: boolean, category: unknown, numeric: number): [unknown, number] | [number, unknown] {
  return vertical ? [category, numeric] : [numeric, category];
}

function inflate(box: { left: number; top: number; right: number; bottom: number }) {
  const next = { ...box };
  if (next.right - next.left < 4) {
    next.left -= 2;
    next.right += 2;
  }
  if (next.bottom - next.top < 4) {
    next.top -= 2;
    next.bottom += 2;
  }
  return next;
}

export function marksFromChart(
  chart: PixelChart,
  origin: Origin,
  card: { id: string; title: string },
): Array<WandElement> {
  const option = chart.getOption();
  const seriesList = option.series ?? [];
  const xCategories = option.xAxis?.[0]?.data;
  const yCategories = option.yAxis?.[0]?.data;
  const vertical = !categoryOnY(option);
  const elements: Array<WandElement> = [];

  seriesList.forEach((series, seriesIndex) => {
    const id = typeof series.id === 'string' ? series.id : '';
    if (id.startsWith('lens-partial:')) {
      return;
    }
    const data = Array.isArray(series.data) ? series.data : [];
    const type = typeof series.type === 'string' ? series.type : '';
    const name = seriesName(series);
    const yAxisIndex = typeof series.yAxisIndex === 'number' ? series.yAxisIndex : 0;
    const categories = vertical ? xCategories : yCategories;

    if (type === 'pie') {
      data.forEach((datum, dataIndex) => {
        const record = datum as { name?: unknown; value?: unknown };
        const numeric = asNumber(record?.value);
        const sector = chart.pieSector?.(seriesIndex, dataIndex) ?? null;
        const local = sector ? pieCentroid(sector) : null;
        const pixel = local ?? toPixel(chart, seriesIndex, yAxisIndex, dataIndex);
        if (!pixel || numeric === null) {
          return;
        }
        const [x, y] = local
          ? [origin.left + local.x, origin.top + local.y]
          : [origin.left + pixel[0], origin.top + pixel[1]];
        elements.push({
          cardId: card.id,
          cardTitle: card.title,
          kind: 'chart-mark',
          label: typeof record.name === 'string' ? record.name : name,
          value: formatMarkNumber(numeric),
          hit: { shape: 'disk', x, y, r: 16 },
        });
      });
      return;
    }

    if (type === 'heatmap') {
      data.forEach((datum) => {
        if (!Array.isArray(datum) || datum.length < 3) {
          return;
        }
        const xIndex = asNumber(datum[0]);
        const yIndex = asNumber(datum[1]);
        const numeric = asNumber(datum[2]);
        if (xIndex === null || yIndex === null || numeric === null) {
          return;
        }
        const here = toPixel(chart, seriesIndex, yAxisIndex, [xIndex, yIndex]);
        if (!here) {
          return;
        }
        const right = toPixel(chart, seriesIndex, yAxisIndex, [xIndex + 1, yIndex]);
        const below = toPixel(chart, seriesIndex, yAxisIndex, [xIndex, yIndex + 1]);
        const halfWidth = right ? Math.abs(right[0] - here[0]) / 2 : 12;
        const halfHeight = below ? Math.abs(below[1] - here[1]) / 2 : 12;
        const xLabel = categoryAt(xCategories, xIndex);
        const yLabel = categoryAt(yCategories, yIndex);
        elements.push({
          cardId: card.id,
          cardTitle: card.title,
          kind: 'chart-mark',
          label: [yLabel, xLabel].filter(Boolean).join(' · ') || name,
          value: formatMarkNumber(numeric),
          hit: {
            shape: 'box',
            box: inflate({
              left: origin.left + here[0] - halfWidth,
              right: origin.left + here[0] + halfWidth,
              top: origin.top + here[1] - halfHeight,
              bottom: origin.top + here[1] + halfHeight,
            }),
          },
        });
      });
      return;
    }

    if (type === 'boxplot') {
      data.forEach((datum, dataIndex) => {
        if (!Array.isArray(datum) || datum.length < 5) {
          return;
        }
        const stats = datum.map(asNumber);
        if (stats.some((value) => value === null)) {
          return;
        }
        const category = categoryAt(xCategories, dataIndex) || String(dataIndex);
        const min = stats[0];
        const median = stats[2];
        const max = stats[4];
        const low = toPixel(chart, seriesIndex, yAxisIndex, tuple(true, xCategories?.[dataIndex] ?? dataIndex, min));
        const high = toPixel(chart, seriesIndex, yAxisIndex, tuple(true, xCategories?.[dataIndex] ?? dataIndex, max));
        const mid = toPixel(chart, seriesIndex, yAxisIndex, tuple(true, xCategories?.[dataIndex] ?? dataIndex, median));
        const anchor = mid ?? high ?? low;
        if (!anchor) {
          return;
        }
        const top = origin.top + Math.min(low?.[1] ?? anchor[1], high?.[1] ?? anchor[1]);
        const bottom = origin.top + Math.max(low?.[1] ?? anchor[1], high?.[1] ?? anchor[1]);
        elements.push({
          cardId: card.id,
          cardTitle: card.title,
          kind: 'chart-mark',
          label: category,
          value: `median ${formatMarkNumber(median)}`,
          hit: {
            shape: 'box',
            box: inflate({
              left: origin.left + anchor[0] - 12,
              right: origin.left + anchor[0] + 12,
              top,
              bottom,
            }),
          },
        });
      });
      return;
    }

    if (type === 'scatter') {
      const xIsCategory = option.xAxis?.[0]?.type === 'category';
      data.forEach((datum) => {
        if (!Array.isArray(datum) || datum.length < 2) {
          return;
        }
        const x = asNumber(datum[0]);
        const y = asNumber(datum[1]);
        if (x === null || y === null) {
          return;
        }
        const pixel = toPixel(chart, seriesIndex, yAxisIndex, [x, y]);
        if (!pixel) {
          return;
        }
        const category = xIsCategory ? categoryAt(xCategories, x) : '';
        elements.push({
          cardId: card.id,
          cardTitle: card.title,
          kind: 'chart-mark',
          label: category || name,
          value: category ? formatMarkNumber(y) : `${formatMarkNumber(x)}, ${formatMarkNumber(y)}`,
          hit: { shape: 'disk', x: origin.left + pixel[0], y: origin.top + pixel[1], r: 10 },
        });
      });
      return;
    }

    const points: Array<{ dataIndex: number; x: number; y: number; label: string; value: string; box: boolean }> = [];
    data.forEach((datum, dataIndex) => {
      if (datum === null || datum === undefined) {
        return;
      }
      let numeric: number | null = null;
      let category = categoryAt(categories, dataIndex);
      let axisCategory: unknown = categories?.[dataIndex] ?? dataIndex;
      if (typeof datum === 'number') {
        numeric = asNumber(datum);
      } else if (Array.isArray(datum) && datum.length >= 2) {
        numeric = asNumber(datum[1]);
        axisCategory = datum[0];
        if (!category) {
          category = String(datum[0]);
        }
      } else if (typeof datum === 'object' && datum && 'value' in datum) {
        numeric = asNumber((datum as { value?: unknown }).value);
        const named = (datum as { name?: unknown }).name;
        if (typeof named === 'string') {
          category = named;
        }
      }
      if (numeric === null) {
        return;
      }
      const here = toPixel(chart, seriesIndex, yAxisIndex, tuple(vertical, axisCategory, numeric));
      if (!here) {
        return;
      }
      if (type === 'bar') {
        const base = toPixel(chart, seriesIndex, yAxisIndex, tuple(vertical, axisCategory, 0));
        const neighborCategory = categories?.[dataIndex + 1] ?? categories?.[dataIndex - 1];
        const neighbor =
          neighborCategory !== undefined
            ? toPixel(chart, seriesIndex, yAxisIndex, tuple(vertical, neighborCategory, numeric))
            : null;
        const delta = neighbor ? (vertical ? Math.abs(neighbor[0] - here[0]) : Math.abs(neighbor[1] - here[1])) : 0;
        const half = delta ? Math.min(28, Math.max(6, delta * 0.32)) : 10;
        const alongStart = base ? Math.min(here[vertical ? 1 : 0], base[vertical ? 1 : 0]) : here[vertical ? 1 : 0];
        const alongEnd = base ? Math.max(here[vertical ? 1 : 0], base[vertical ? 1 : 0]) : here[vertical ? 1 : 0];
        const box = vertical
          ? {
              left: origin.left + here[0] - half,
              right: origin.left + here[0] + half,
              top: origin.top + alongStart,
              bottom: origin.top + alongEnd,
            }
          : {
              left: origin.left + alongStart,
              right: origin.left + alongEnd,
              top: origin.top + here[1] - half,
              bottom: origin.top + here[1] + half,
            };
        elements.push({
          cardId: card.id,
          cardTitle: card.title,
          kind: 'chart-mark',
          label: [name, category].filter(Boolean).join(' · '),
          value: formatMarkNumber(numeric),
          hit: { shape: 'box', box: inflate(box) },
        });
        return;
      }
      points.push({
        dataIndex,
        x: origin.left + here[0],
        y: origin.top + here[1],
        label: [name, category].filter(Boolean).join(' · '),
        value: formatMarkNumber(numeric),
        box: false,
      });
    });

    points.forEach((point, index) => {
      const previous = points[index - 1];
      const next = points[index + 1];
      const prev =
        previous && point.dataIndex === previous.dataIndex + 1 ? { x: previous.x, y: previous.y } : undefined;
      const nextPoint = next?.dataIndex === point.dataIndex + 1 ? { x: next.x, y: next.y } : undefined;
      elements.push({
        cardId: card.id,
        cardTitle: card.title,
        kind: 'chart-mark',
        label: point.label,
        value: point.value,
        hit: { shape: 'disk', x: point.x, y: point.y, r: 12, prev, next: nextPoint },
      });
    });
  });

  if (elements.length === 0 && origin.width > 1 && origin.height > 1) {
    elements.push({
      cardId: card.id,
      cardTitle: card.title,
      kind: 'chart',
      label: card.title,
      value: 'plot',
      hit: {
        shape: 'box',
        box: {
          left: origin.left,
          top: origin.top,
          right: origin.left + origin.width,
          bottom: origin.top + origin.height,
        },
      },
    });
  }
  return elements;
}

export function formatMarkNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return String(Math.round(value * 100) / 100);
}

export function pieCentroid(sector: PieSector): WandPoint {
  const mid = (sector.startAngle + sector.endAngle) / 2;
  const reach = ((sector.r0 ?? 0) + sector.r) / 2;
  return {
    x: sector.cx + Math.cos(mid) * reach,
    y: sector.cy + Math.sin(mid) * reach,
  };
}
